import axios from 'axios'
import { reactive } from 'vue'
import { topics, fallback, smallTalk } from './knowledge'

// The assistant talks to POST /api/assistant/ask, which forwards the question to the configured AI
// model (Kimi by default; see the "Assistant" section of appsettings.json). When no model is
// connected, or it is unavailable, replies come from the local help guide in ./knowledge.js.
export const assistantState = reactive({ simulated: true, checked: false })

export async function checkStatus () {
  try {
    const { data } = await axios.get('/assistant/status')
    assistantState.simulated = !(data && data.enabled)
  } catch (e) {
    assistantState.simulated = true
  } finally {
    assistantState.checked = true
  }
  return !assistantState.simulated
}

// ---- local fallback ----

function tokenize (text) {
  return text.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(Boolean)
}

function score (question, topic) {
  const words = tokenize(question)
  const padded = ' ' + words.join(' ') + ' '
  const wordSet = new Set(words)
  let total = 0
  topic.keywords.forEach(keyword => {
    const parts = tokenize(keyword)
    if (!parts.length) return
    if (padded.includes(' ' + parts.join(' ') + ' ')) total += 2 + parts.length
    else if (parts.length > 1 && parts.every(part => wordSet.has(part))) total += 2
  })
  return total
}

function match (question) {
  let best = null
  let bestScore = 0
  topics.forEach(topic => {
    const s = score(question, topic)
    if (s > bestScore) {
      best = topic
      bestScore = s
    }
  })
  return bestScore >= 3 ? best : null
}

function localReply (message) {
  const delay = 600 + Math.random() * 700
  return new Promise(resolve => {
    setTimeout(() => {
      const trimmed = (message || '').trim()
      const chat = smallTalk.find(item => item.pattern.test(trimmed))
      if (chat && trimmed.split(/\s+/).length <= 4) {
        resolve({ text: chat.answer, topic: null, simulated: true })
        return
      }
      const topic = match(trimmed)
      resolve(topic ? { text: topic.answer, topic: topic.id, simulated: true } : { text: fallback, topic: null, simulated: true })
    }, delay)
  })
}

// ---- public API ----

// history: previous chat messages as { role: 'user' | 'assistant', text }
export async function ask (message, history = []) {
  if (!assistantState.simulated) {
    try {
      const turns = history.slice(0, -1).map(m => ({ role: m.role, text: m.text }))
      const { data } = await axios.post('/assistant/ask', { message, history: turns })
      return { text: data.text, topic: null, simulated: false }
    } catch (e) {
      const status = e.response && e.response.status
      const serverMessage = e.response && e.response.data && e.response.data.error
      // the user did something we can explain (too many questions, message too long)
      if ((status === 429 || status === 400) && serverMessage) {
        return { text: serverMessage, topic: null, simulated: false }
      }
      // provider down or not reachable: answer from the local guide instead
      const reply = await localReply(message)
      reply.text = 'The AI assistant is unavailable right now, so here is a basic answer.\n\n' + reply.text
      return reply
    }
  }
  return localReply(message)
}
